// Typed contract for the evaluation-draft body + save response (Story 6.2's
// save_evaluation_draft slice). Story 6.4 introduces it for the task-selection step;
// 6.3 (diagnosis), 6.5 (scoring) and 6.7 (resume) extend/reuse it as the stepper draft
// state grows. Mirrors save_evaluation_draft/schemas.py::EvaluationDraft.
//
// FULL-REPLACE CONTRACT: a save REPLACES the entire draft_data — omitted optional fields
// are CLEARED server-side. Callers MUST send the WHOLE current draft on every save, never
// a delta (see useSaveEvaluationDraft). That is why the Task step is presentational over
// lifted state: it never composes a `{tasks}`-only body.

/** The (optional) diagnosis selection — all parts optional while drafting (composed in 6.3). */
export interface DiagnosisDraft {
  group?: string | null;
  specific?: string | null;
  subtype?: string | null;
  location?: string | null;
}

/**
 * One AMPS task selection (FR18 — the canonical `task_code` + its `task_category`).
 *
 * Doubles as the Task step's UI CARD model: while editing, a card may be PARTIAL —
 * `task_category` chosen but `task_code` still "" (no specific task yet). Only fully
 * selected cards (non-empty `task_code`) are persisted to the draft `tasks` array
 * (AC 6) — see `persistableTasks` in the Task step.
 */
export interface SelectedTask {
  task_category: string;
  task_code: string;
}

/** The whole mutable draft body for `POST /api/v1/evaluations/{id}/save-draft`. */
export interface EvaluationDraftBody {
  evaluation_date?: string | null;
  context?: string | null;
  notes?: string | null;
  diagnosis?: DiagnosisDraft | null;
  /** 0–4 fully-selected AMPS tasks (FR18). The ≥2 floor is a submit-time rule (6.6). */
  tasks: SelectedTask[];
  /** Per-task ADL score arrays keyed by task code (UX D2); 1–4 or null (unscored). */
  scores: Record<string, (number | null)[]>;
  /**
   * The stepper position the clinician last viewed (FE-owned UI state). Persisted into
   * draft_data on every save so Story 6.7 resume re-opens the EXACT step
   * (`resolveResumeStep` honours it over the score-derivation, which alone can never
   * reach review/results). The backend stores it opaquely (bounded text, no step enum).
   */
  current_step?: string | null;
}

/** PHI-free projection returned after a successful save (the next save's X-Client-Version). */
export interface SaveDraftResponse {
  id: string;
  version: number;
  updated_at: string;
  state: string;
}

/**
 * The coded error envelopes the save-draft path can return (ApiError.code). 6.3/6.5/6.7
 * reuse these to map a failure to corrective UX instead of a generic error.
 */
export const EVAL_ERROR = {
  /**
   * A selected task has 2+ unscored ADL items at submit (< 34/35). Story 6.6's submit gate;
   * FE-addressable so Story 6.8's Validation Banner + Story 9.5's submit CTA can map it to
   * corrective per-task UX. 6.6 itself wires no submit button — the scoring-step advisory
   * derives the same state locally (see scoring-validation.ts).
   */
  INSUFFICIENT_ADL_ITEMS: "EVAL-001",
  /** Stale X-Client-Version (or a concurrent save won the CAS race) — reconcile from server_state. */
  VERSION_CONFLICT: "EVAL-002",
  /** Evaluation missing / not owned (RLS existence-hiding). */
  NOT_FOUND: "EVAL-003",
  /** A score value outside the 1–4 integer range (Story 6.5 — unreachable from the FE per AC 3). */
  SCORE_OUT_OF_RANGE: "EVAL-004",
  /** The same task_code selected twice in one draft. */
  DUPLICATE_TASK: "EVAL-007",
  /** A task_code not present in the active AMPS catalogue. */
  UNKNOWN_TASK_CODE: "EVAL-008",
  /**
   * Save-draft / submit to a non-draft (submitted/scored/archived) evaluation — 410 Gone.
   * Story 6.6's deferred guard; Story 7.4's offline-drain silent-adoption branch keys off it.
   */
  ALREADY_SUBMITTED: "EVAL-009",
  /**
   * Fewer than 2 fully-selected tasks at submit (the FR18 ≥2 floor). Story 6.6's submit gate;
   * FE-addressable so Story 6.8's Validation Banner + Story 9.5's submit CTA can map it to a
   * corrective "select at least 2 tasks" blocker (`details.task_count`, `minimum_required: 2`).
   */
  INSUFFICIENT_TASKS: "EVAL-010",
  /**
   * Draft submitted without an `evaluation_date` (backend `submit_evaluation/command.py`
   * step 4b, 2026-07-14 sweep). FE-addressable: the Validation Banner blocks the press via
   * `computeSubmitReadiness`'s date blocker, and `mapSubmitErrorToBlockers` maps this 422
   * to the identical blocker (seam-eval013 server parity).
   */
  MISSING_EVALUATION_DATE: "EVAL-013",
} as const;

/** The full server draft echoed back on a 409 EVAL-002 so the client can reconcile (AC 8). */
export interface VersionConflictServerState {
  version: number;
  state: string;
  draft_data: EvaluationDraftBody;
}
